// Filter.jsx
import React from 'react';
import Selector from 'components/shared/atoms/Selector/Selector';
import { DateRangePicker } from './DateRangePicker/DateRangePicker';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import './Filter.scss';

/**
 * @typedef {Object} DateRangeValue
 * @property {string} from - The start date of the selected range
 * @property {string} to - The end date of the selected range
 */

/** * Filter component for booking search and filtering
 * @param {Object} props - Component properties
 * @param {Array} props.data - List of booking data
 * @param {string[]} props.serviceTypeList - List of service types
 * @param {string[]} props.statusMap - List of service statuses
 * @param {Function} props.handleOnChangeSearchFilter - Callback function to handle search filter changes
 * @returns {JSX.Element} Rendered Filter component
 */
function Filter({ data, serviceTypeList, statusMap, handleOnChangeSearchFilter, globalFilterDefaultValues, objStatusMapRaw }) {
    const [filterDefaultValues, setFilterDefaultValues] = React.useState(globalFilterDefaultValues || {});
    const [filterValues, setFilterValues] = React.useState({
        serviceType: globalFilterDefaultValues?.service_type.replace('*', '&'),
        dateCreated: {
            from: globalFilterDefaultValues.date_created_from,
            to: globalFilterDefaultValues.date_created_to
        },
        serviceStatus: globalFilterDefaultValues.service_status_label,
        searchTerm: globalFilterDefaultValues.search_term,
        serviceStatusLabel: globalFilterDefaultValues.service_status_label || ''
    });
    const [showFilters, setShowFilters] = React.useState(false);
    const [hasActiveFilters, setHasActiveFilters] = React.useState(false);

    React.useEffect(() => {
        const filterValuesWithoutSearchTerm = { ...filterValues };
        delete filterValuesWithoutSearchTerm.searchTerm;

        const active = Object.values(filterValuesWithoutSearchTerm).some(val => {
            if (typeof val === 'object' && val !== null) {
                return Object.values(val).some(v => v !== '' && v !== null && v !== undefined);
            }
            return val !== '' && val !== null && val !== undefined;
        });
        setHasActiveFilters(active);
    }, [filterValues]);

    React.useEffect(() => {
        setShowFilters(hasActiveFilters);
    }, [hasActiveFilters]);

    React.useEffect(() => {
        setFilterDefaultValues({
            serviceType: [{ name: 'All', value: '' }, ...serviceTypeList.map((_type) => ({
                name: _type,
                value: _type
            }))],
            dateCreated: {
                from: '',
                to: ''
            },
            serviceStatus: [{ name: 'All', value: '' }, ...statusMap.map((_s) => ({
                name: _s,
                value: _s
            }))],
            searchTerm: ''
        });
    }, [data]);

    /**
     * Handles the change event for the search input and updates the filter values state.
     *
     * @param {React.ChangeEvent<HTMLInputElement>} event - The change event from the input element.
     * @description Handles the change event for the search input and updates the filter values state.
     * 
     * It updates the filterValues state with the new value for the corresponding input field.
     * @returns {void}
     */
    const handleOnChangeSearch = (event) => {
        const { name, value } = event.target;
        if (name === 'serviceStatus') {
            setFilterValues((prevValues) => ({
                ...prevValues,
                serviceStatusLabel: value || ''
            }));
        }
        setFilterValues((prevValues) => ({
            ...prevValues,
            [name]: value
        }));
    };

    /**
     * Handles the change event for the date range picker and updates the filter values state.
     *
     * @param {DateRangeValue} value - The selected date range object. 
     *   - `value.from`: The start date of the selected range (YYYY-MM-DD).
     *   - `value.to`: The end date of the selected range (YYYY-MM-DD).
     * @description Handles the change event for the date range picker and updates the filterValues state
     * with the new date range for 'dateCreated'.
     * @returns {void}
     */
    const handleDateRangeChange = (value) => {
        setFilterValues((prevValues) => ({
            ...prevValues,
            dateCreated: value
        }));
    };

    React.useEffect(() => {
        handleOnChangeSearchFilter(filterValues);
    }, [filterValues]);

    // React.useEffect(() => {
    //     showFilters
    // }, [showFilters]);

    return (
        <div className="tool-bar">
            <div className="item item--search animated-entrance">
                <label htmlFor="searchTerm">SEARCH</label>
                <input
                className="item__input"
                placeholder='Search for Booking ID or Owner Name'
                type="search"
                name="searchTerm"
                id="searchTerm"
                value={filterValues.searchTerm}
                onChange={handleOnChangeSearch}
                />
            </div>
            <a
                className="toggle-filters-btn"
                onClick={() => setShowFilters(v => !v)}
                aria-expanded={showFilters}
                aria-controls="advanced-filters"
            >
                {showFilters ? 
                    <div className='btn-icon'>
                        <span>Advanced Filter</span>
                        <FaChevronUp size={11}/>
                    </div> : 
                    <div className='btn-icon'>
                        <span>Advanced Filter</span>
                        <FaChevronDown size={11}/>
                    </div>
                    }
            </a>
            {showFilters && (
                <div className="advanced-filters" id="advanced-filters">
                    <div className="item animated-entrance">
                        <label htmlFor="serviceType">SERVICE TYPE</label>
                        {filterDefaultValues.serviceType.length > 1 ? (
                            <Selector
                                name="serviceType"
                                placeholder='All'
                                hideIcon={true}
                                value={filterValues.serviceType}
                                options={filterDefaultValues.serviceType}
                                onChange={(val) => handleOnChangeSearch({ target: { name: 'serviceType', value: val } })}
                            />
                        ) : (
                            <SkeletonFilterInput />
                        )}
                    </div>
                    <div className="item animated-entrance-2">
                        <label htmlFor="dateCreated">DATE CREATED</label>
                        {/* If you have async date options, use skeleton, else show DateRangePicker */}
                        <DateRangePicker
                            name="dateCreated"
                            id="dateCreated"
                            value={filterValues.dateCreated}
                            onChange={handleDateRangeChange}
                        />
                    </div>
                    <div className="item animated-entrance-3">
                        <label htmlFor="serviceStatus">STATUS</label>
                        {filterDefaultValues.serviceStatus.length > 1 ? (
                            <Selector
                                name="serviceStatus"
                                placeholder='All'
                                hideIcon={true}
                                value={filterValues.serviceStatus}
                                options={filterDefaultValues.serviceStatus}
                                onChange={(val) => handleOnChangeSearch({ target: { name: 'serviceStatus', value: val } })}
                            />
                        ) : (
                            <SkeletonFilterInput />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function SkeletonFilterInput() {
    return <div className="skeleton-filter-input" />;
}

export default Filter;
